import ImageComponent from '@/components/ImageComponent';
import type { CsnSearchVenue } from '@/models/CsnSearchVenue';
import type { ImageProps } from 'next/image';
import placeholder from '@/assets/images/placeholder.png';
import { formatMessage } from '@/utils/translationHelper';
import type { Locale } from '@/i18n';
import { getLocation } from '@/utils/getVenueLocation';
import RequestQuoteButton from '@/components/RequestQuoteButton';
import type { ShoppingCartItem } from '@/models/ShoppingCartItem';
import type { MeasurementUnit } from '@/models/Filter';
import { type ReactNode, useMemo } from 'react';
import RatingReviewDistance from '@/components/card/RatingReviewDistance';
import type { FullMapListViewStrings } from '@/components/maps/list-view/FullMapListView';
import { getVenueUrl } from '@/utils/venueDetailsHelper';
import type { SearchParams } from '@/models/SearchParams';
import Link from 'next/link';

type FullMapVenueCardProps = {
  children: ReactNode;
  venue: CsnSearchVenue;
  venueIndex: number;
  locale: Locale;
  isLoggedIn: boolean;
  hideVenueStats: boolean;
  activeFilters: string;
  searchParams: SearchParams;
  cventGuestDomain: string;
  basePath: string;
  isRFPFullAccess: boolean;
  fullMapListViewStrings: FullMapListViewStrings;
  shoppingCartItem?: ShoppingCartItem;
  measurementUnit: MeasurementUnit;
  canShowDistanceOnCards: boolean;
};

export default function FullMapVenueCard({
  children,
  venue,
  venueIndex,
  locale,
  isLoggedIn,
  hideVenueStats,
  activeFilters,
  searchParams,
  cventGuestDomain,
  basePath,
  isRFPFullAccess,
  fullMapListViewStrings,
  shoppingCartItem,
  measurementUnit,
  canShowDistanceOnCards
}: FullMapVenueCardProps): JSX.Element {
  const { ratingAndReviewStrings, requestQuoteButtonStrings, venueImageAltText, screenReaderText } =
    fullMapListViewStrings;

  const venueLocation = useMemo(
    () => getLocation(venue.city, venue.stateProvinceCode, venue.countryCode),
    [venue.city, venue.countryCode, venue.stateProvinceCode]
  );

  let imgAltText = venue.name;
  if (venueLocation) {
    imgAltText = formatMessage(venueImageAltText, locale, {
      venueName: venue.name,
      venueLocation
    });
  }
  const venueImageSrc = useMemo(
    () =>
      venue.mainImage?.large ??
      venue.mainImage?.original ??
      venue.mainImage?.medium ??
      venue.mainImage?.small ??
      venue.mainImage?.extraSmall ??
      venue.firstProfileImageUrl,
    [
      venue.firstProfileImageUrl,
      venue.mainImage?.extraSmall,
      venue.mainImage?.large,
      venue.mainImage?.medium,
      venue.mainImage?.original,
      venue.mainImage?.small
    ]
  );
  const imageProps: ImageProps = {
    fill: true,
    sizes: '106px',
    style: { objectFit: 'cover' },
    src: venue.displaySearchResultImage && venueImageSrc ? venueImageSrc : placeholder.src,
    alt: imgAltText
  };

  const isMicrosite = venue?.listingOwnerType === 'MICROSITE';
  const venueUrl = isMicrosite
    ? getVenueUrl(venue, locale, cventGuestDomain, basePath)
    : getVenueUrl(venue, locale, cventGuestDomain, basePath, searchParams);

  return (
    <div
      className="my-4 flex h-[152px] w-full flex-col justify-between gap-1 rounded-lg bg-background shadow-2"
      data-cvent-id={`list-view-venue-card-full-map-${venue.id}`}
    >
      <div className="flex min-h-[80px] w-full gap-2">
        <div className="relative w-[106px] flex-shrink-0 overflow-hidden rounded-br-md rounded-tl-md">
          <ImageComponent imageProps={imageProps} />
        </div>
        <div className="flex h-full w-full flex-col justify-between overflow-hidden p-2 pl-0">
          <div className="flex justify-between">
            <Link href={venueUrl} className="w-5/6">
              <h3 className="line-clamp-2 break-words text-b-base/normal text-hb-brand-60" title={venue.name}>
                {venue.name}
              </h3>
            </Link>
            {shoppingCartItem && (
              <div>
                <RequestQuoteButton
                  activeFilters={activeFilters}
                  strings={requestQuoteButtonStrings}
                  item={shoppingCartItem}
                  iconOnly
                  className="!h-6 !min-h-0 !w-6 !rounded-sm"
                  isRFPFullAccess={isRFPFullAccess}
                  displayTypes={venue.displayTypes}
                  isLoggedIn={isLoggedIn}
                  testID={`full-map-venue-container-${venueIndex + 1}`}
                />
              </div>
            )}
          </div>
          {!hideVenueStats && (
            <RatingReviewDistance
              ratings={venue.preferredRating}
              reviews={venue.plannerReviewsRating}
              screenReaderText={screenReaderText}
              measurementUnit={measurementUnit}
              showDistance={
                canShowDistanceOnCards && venue.type.toString() !== 'CVB' && venue.type.toString() !== 'DMC'
              }
              distanceInMetric={venue.distanceFromSearchLocation?.metricValue}
              distanceInImperial={venue.distanceFromSearchLocation?.imperialValue}
              locale={locale}
              strings={ratingAndReviewStrings}
              isListViewInFullMap
            />
          )}
        </div>
      </div>
      {children}
    </div>
  );
}
